import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useAdminPartnersList } from "@/features/admin/partners/use-admin-partners";
import { useCreateAllocation } from "./use-create-allocation";

// Sub-bucket 15 — port of /app/admin/mdf/[id]/_components/AddAllocationDialog.tsx.
//
// Partner picker is a search + scrollable list (clone of the legacy approach)
// rather than a heavyweight combobox primitive. Partners with an existing
// allocation in this fund are disabled (the DB has unique(fund,partner) so
// the service would reject; surfacing the gate up-front saves a 400 round-trip).
//
// The partner source is the already-admin-cached `useAdminPartnersList()` —
// pulls more fields than strictly needed but reuses an existing cache so
// opening the dialog doesn't trigger a fresh round-trip in the common case.

export function AddAllocationDialog({
  fundId,
  existingPartnerIds,
  open,
  onOpenChange,
}: {
  fundId: string;
  existingPartnerIds: string[];
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const partnersQuery = useAdminPartnersList();
  const create = useCreateAllocation();
  const [search, setSearch] = useState("");
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [cap, setCap] = useState("");
  const [notes, setNotes] = useState("");

  const partnerOptions = useMemo(() => {
    const allocatedSet = new Set(existingPartnerIds);
    return (partnersQuery.data ?? []).map((p) => ({
      id: p.id,
      company_name: p.company_name,
      alreadyAllocated: allocatedSet.has(p.id),
    }));
  }, [partnersQuery.data, existingPartnerIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return partnerOptions;
    return partnerOptions.filter((p) =>
      p.company_name.toLowerCase().includes(q),
    );
  }, [partnerOptions, search]);

  function reset() {
    setSearch("");
    setPartnerId(null);
    setCap("");
    setNotes("");
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!partnerId) {
      toast.error("Pick a partner");
      return;
    }
    const capParsed = Number(cap);
    if (!Number.isInteger(capParsed) || capParsed < 0) {
      toast.error("Cap must be a whole number of EUR");
      return;
    }
    create.mutate(
      {
        fundId,
        input: {
          partner_id: partnerId,
          cap_amount_eur: capParsed,
          notes: notes.trim() ? notes.trim() : null,
        },
      },
      {
        onSuccess: () => {
          toast.success("Allocation added");
          onOpenChange(false);
          reset();
        },
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to add allocation")),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Add allocation
          </DialogTitle>
          <DialogDescription>
            Allocate a portion of this fund to a partner. The partner can then
            submit requests up to the cap.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="mdf-alloc-partner-search">Partner</Label>
            <Input
              id="mdf-alloc-partner-search"
              type="search"
              placeholder="Search partners…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={partnersQuery.isLoading}
            />
            <div className="max-h-[200px] overflow-y-auto rounded-md border">
              {partnersQuery.isLoading ? (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Loading partners…
                </p>
              ) : filtered.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No partners match this search.
                </p>
              ) : (
                <ul className="divide-y">
                  {filtered.map((p) => {
                    const disabled = p.alreadyAllocated;
                    const selected = partnerId === p.id;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => setPartnerId(p.id)}
                          className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition ${
                            selected
                              ? "bg-accent/10 text-accent"
                              : disabled
                                ? "cursor-not-allowed text-muted-foreground"
                                : "hover:bg-muted"
                          }`}
                        >
                          <span className="font-medium">{p.company_name}</span>
                          {disabled && (
                            <span className="text-[11px] text-muted-foreground">
                              already allocated
                            </span>
                          )}
                          {selected && !disabled && (
                            <span className="text-[11px] font-medium uppercase tracking-wider">
                              Selected
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="mdf-alloc-cap">Cap (EUR)</Label>
            <Input
              id="mdf-alloc-cap"
              type="number"
              min={0}
              step={1}
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              placeholder="1000"
              required
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="mdf-alloc-notes">Notes (optional)</Label>
            <Textarea
              id="mdf-alloc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Scope or restrictions for this partner…"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || !partnerId}
            >
              {create.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add allocation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
